import { BarChart3Icon, BoxesIcon, WorkflowIcon } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type ServiceDetail = {
  slug: 'crm-integrations' | 'automation' | 'lead-scoring'
  eyebrow: string
  title: string
  shortTitle: string
  description: string
  introduction: string
  outcomes: string[]
  delivery: Array<{ step: string; title: string; description: string }>
  icon: LucideIcon
  accent: 'teal' | 'ochre' | 'coral'
}

export const services: ServiceDetail[] = [
  {
    slug: 'crm-integrations',
    eyebrow: 'Flagship service',
    title: 'CRM integrations that make your data useful.',
    shortTitle: 'CRM integrations',
    description:
      'Connect the tools your team relies on, then give every customer interaction a dependable home.',
    introduction:
      'A CRM is only as valuable as the information flowing through it. I design the connections, handoffs, and data rules that make your CRM a reliable operating system for revenue teams.',
    outcomes: [
      'One dependable customer record across your core tools',
      'Clear ownership and context at every handoff',
      'Less manual updating and fewer avoidable data gaps',
    ],
    delivery: [
      {
        step: '01',
        title: 'Map the reality',
        description:
          'We trace your current journey, systems, and the decisions each team needs to make.',
      },
      {
        step: '02',
        title: 'Design the connection',
        description:
          'I define field mappings, ownership rules, and the practical edge cases before anything goes live.',
      },
      {
        step: '03',
        title: 'Build & hand over',
        description:
          'The integration is tested against real workflows, documented, and introduced with a clear operating guide.',
      },
    ],
    icon: BoxesIcon,
    accent: 'teal',
  },
  {
    slug: 'automation',
    eyebrow: 'Systems that follow through',
    title: 'Automation with a human point of view.',
    shortTitle: 'Automation',
    description:
      'Turn repetitive revenue operations into dependable workflows without removing the moments that need judgment.',
    introduction:
      'Good automation makes a team more attentive, not more distant. I identify the repeatable work around your sales and customer processes, then build useful, visible workflows around it.',
    outcomes: [
      'Timely follow-ups without manual chasing',
      'Consistent routing, alerts, and lifecycle actions',
      'Workflows your team can understand and maintain',
    ],
    delivery: [
      {
        step: '01',
        title: 'Find the friction',
        description:
          'We identify the recurring steps, missed handoffs, and slow moments that are worth automating.',
      },
      {
        step: '02',
        title: 'Set the guardrails',
        description:
          'I establish triggers, exceptions, owners, and review points so automation behaves predictably.',
      },
      {
        step: '03',
        title: 'Launch with visibility',
        description:
          'Every workflow is tested, named clearly, and paired with a simple path for your team to inspect it.',
      },
    ],
    icon: WorkflowIcon,
    accent: 'ochre',
  },
  {
    slug: 'lead-scoring',
    eyebrow: 'Signal, not noise',
    title: 'A lead score your team can act on.',
    shortTitle: 'Lead score model',
    description:
      'Build a practical model that helps your team focus attention where fit and intent are strongest.',
    introduction:
      'Lead scoring should support better conversations—not create another dashboard to ignore. I create transparent score models grounded in your actual buying signals, team capacity, and go-to-market motion.',
    outcomes: [
      'A shared definition of qualified interest',
      'Clear priority signals for sales and marketing',
      'A model that can evolve as you learn',
    ],
    delivery: [
      {
        step: '01',
        title: 'Define quality',
        description:
          'We align on what a good-fit account and meaningful engagement actually look like for your business.',
      },
      {
        step: '02',
        title: 'Weight the signals',
        description:
          'I turn fit, behavior, and timing into a focused model with simple, explainable logic.',
      },
      {
        step: '03',
        title: 'Operationalise it',
        description:
          'Scores are connected to routing and follow-up so the model changes what your team does next.',
      },
    ],
    icon: BarChart3Icon,
    accent: 'coral',
  },
]

export function getService(slug: ServiceDetail['slug']) {
  return services.find((service) => service.slug === slug)
}
